import React from "react";
import ShareCardFrame, { CardFooter, Wordmark, scoreColor } from "@/components/share/ShareCardFrame";
import { companyCaptions } from "@/components/share/CaptionBox";

const FOOTER = "Verified via my360boss.com. Cryptographically secure. 100% anonymous worker data.";

export default function CompanyStatCard({ company }) {
  const speak = company.speak_up_score || 0;
  const url = `${window.location.origin}/company/${encodeURIComponent(company.name)}`;
  const t = company.trajectory;

  return (
    <ShareCardFrame fileName={`my360boss-${company.name.replace(/\s+/g, "-").toLowerCase()}`} shareText={`${company.name}: Speak-Up score ${speak}/5, org leadership score ${company.org_score}/5 on my360boss. ${url}`} shareUrl={url} captions={companyCaptions(company, url)} testid="company-stat-card">
      <div className="px-6 pt-6 flex items-center justify-between">
        <Wordmark />
        <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>{company.review_count} reviews · {company.supervisor_count} managers</span>
      </div>

      <div className="px-6 mt-5">
        <h2 className="leading-[0.95] break-words" style={{ fontSize: "2.4rem", fontWeight: 900, letterSpacing: "-0.02em" }} data-testid="company-card-name">{company.name}</h2>
        <div className="mt-2 text-sm" style={{ color: "#94A3B8" }}>{[company.industry, company.location].filter(Boolean).join(" · ")}</div>
      </div>

      <div className="px-6 mt-6 grid grid-cols-2 gap-3">
        <div className="rounded-xl p-4" style={{ backgroundColor: scoreColor(speak) }}>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-900/70">Speak-Up score</p>
          <div className="text-slate-900" style={{ fontSize: "2.6rem", fontWeight: 900, lineHeight: 1 }} data-testid="company-card-speakup">{speak ? speak.toFixed(1) : "—"}</div>
          <p className="text-[11px] text-slate-900/80 mt-1 leading-tight">"I can raise concerns without it being held against me"</p>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: "rgba(248,250,252,0.06)", border: "1px solid rgba(248,250,252,0.12)" }}>
          <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>Org leadership</p>
          <div style={{ fontSize: "2.6rem", fontWeight: 900, lineHeight: 1, color: scoreColor(company.org_score) }}>{company.org_score ? company.org_score.toFixed(1) : "—"}</div>
          <p className="text-[11px] mt-1" style={{ color: "#CBD5E1" }}>{company.recommend_rate}% would recommend</p>
        </div>
      </div>

      <div className="px-6 mt-4">
        {t ? (
          <div data-testid="company-card-trajectory">
            <p className="font-mono text-[10px] uppercase tracking-widest mb-1.5" style={{ color: "#94A3B8" }}>Trajectory · {t.total} check-ins</p>
            <div className="flex h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(248,250,252,0.1)" }}>
              <div style={{ width: `${t.improved_pct}%`, backgroundColor: "#10B981" }} />
              <div style={{ width: `${t.same_pct}%`, backgroundColor: "#94A3B8" }} />
              <div style={{ width: `${t.worse_pct}%`, backgroundColor: "#EF4444" }} />
            </div>
            <div className="flex justify-between text-[11px] mt-1.5" style={{ color: "#CBD5E1" }}>
              <span>▲ {t.improved_pct}% improved</span><span>{t.same_pct}% same</span><span>▼ {t.worse_pct}% worse</span>
            </div>
          </div>
        ) : (
          <p className="text-[11px]" style={{ color: "#64748B" }}>Trajectory appears once employees check back in.</p>
        )}
        {company.top_tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {company.top_tags.map((x) => <span key={x.tag} className="text-[11px] font-bold px-3 py-1 rounded-full" style={{ backgroundColor: "#EF4444", color: "#fff" }}>[{x.tag} ×{x.count}]</span>)}
          </div>
        )}
      </div>

      <CardFooter text={FOOTER} />
    </ShareCardFrame>
  );
}
